import React, { useState } from 'react';
import { FileText, Eye, Pencil, Trash2, ArrowRightCircle, Search } from 'lucide-react';
import { useData } from '../context/DataContext';
import { computeTotals, fmtRM2, fmtDate } from '../lib/store';
import { PageHeader, StatusBadge, EmptyState, Card } from '../components/Shared';
import { LineItemDialog, DocumentPreview } from '../components/DocumentDialog';
import { Input } from '../components/ui/input';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '../components/ui/table';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

export default function Quotations() {
  const { quotations, company, getCustomer, getNextNumber, addItem, updateItem, removeItem, convertQuotationToInvoice, consumeNumber } = useData();
  const [edit, setEdit] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  const [preview, setPreview] = useState(null);
  const [tab, setTab] = useState('all');
  const [q, setQ] = useState('');

  const blank = () => ({
    id: null, number: getNextNumber(company.quotationPrefix, 'quotations'),
    customerId: '', date: new Date().toISOString().slice(0, 10),
    validUntil: '', status: 'draft', items: [{ productId: '', name: '', qty: 1, price: 0, taxable: true }], notes: '',
  });

  const openNew = () => { setEdit(blank()); setEditOpen(true); };
  const openEdit = (item) => { setEdit(item); setEditOpen(true); };

  const save = (form) => {
    if (form.id) { updateItem('quotations', form.id, form); toast.success('Quotation updated'); }
    else { addItem('quotations', form); consumeNumber('quotations', form.number); toast.success('Quotation created'); }
    setEditOpen(false);
  };

  const convert = (item) => {
    const inv = convertQuotationToInvoice(item);
    toast.success(`Converted to invoice ${inv.number}`);
  };

  const filtered = quotations
    .filter((x) => tab === 'all' || x.status === tab)
    .filter((x) => (getCustomer(x.customerId)?.name || '').toLowerCase().includes(q.toLowerCase()) || (x.number || '').toLowerCase().includes(q.toLowerCase()));

  return (
    <div className="animate-fade-in-up">
      <PageHeader title="Quotations" subtitle="Professional quotes — PDF, email or WhatsApp." actionLabel="New Quotation" onAction={openNew} />

      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList>
            {['all', 'draft', 'pending', 'accepted', 'rejected'].map((t) => (
              <TabsTrigger key={t} value={t} className="capitalize">{t}</TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
        <div className="relative max-w-xs">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search..." className="pl-9" />
        </div>
      </div>

      {filtered.length === 0 ? (
        <EmptyState icon={FileText} title="No quotations" subtitle="Create your first quotation." />
      ) : (
        <Card className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50 hover:bg-slate-50">
                <TableHead className="font-semibold text-slate-600">Number</TableHead>
                <TableHead className="font-semibold text-slate-600">Customer</TableHead>
                <TableHead className="font-semibold text-slate-600">Date</TableHead>
                <TableHead className="font-semibold text-slate-600">Status</TableHead>
                <TableHead className="text-right font-semibold text-slate-600">Total</TableHead>
                <TableHead className="text-right font-semibold text-slate-600">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((item) => {
                const totals = computeTotals(item.items, company.sstRate);
                return (
                  <TableRow key={item.id} className="hover:bg-slate-50">
                    <TableCell className="font-semibold text-blue-600">{item.number || '—'}</TableCell>
                    <TableCell className="text-slate-700">{getCustomer(item.customerId)?.name || '—'}</TableCell>
                    <TableCell className="text-slate-500">{fmtDate(item.date)}</TableCell>
                    <TableCell><StatusBadge status={item.status} /></TableCell>
                    <TableCell className="text-right font-semibold text-slate-800">{fmtRM2(totals.total)}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-0.5">
                        <button title="Preview" onClick={() => setPreview(item)} className="rounded-md p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700"><Eye className="h-4 w-4" /></button>
                        <button title="Edit" onClick={() => openEdit(item)} className="rounded-md p-1.5 text-slate-400 hover:bg-slate-100 hover:text-blue-600"><Pencil className="h-4 w-4" /></button>
                        {item.status !== 'rejected' && <button title="Convert to Invoice" onClick={() => convert(item)} className="rounded-md p-1.5 text-slate-400 hover:bg-emerald-50 hover:text-emerald-600"><ArrowRightCircle className="h-4 w-4" /></button>}
                        <button title="Delete" onClick={() => { removeItem('quotations', item.id); toast.success('Deleted'); }} className="rounded-md p-1.5 text-slate-400 hover:bg-red-50 hover:text-red-600"><Trash2 className="h-4 w-4" /></button>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Card>
      )}

      <LineItemDialog open={editOpen} onOpenChange={setEditOpen} initial={edit} onSave={save}
        title={edit?.id ? 'Edit Quotation' : 'New Quotation'} extraDates={{ label: 'Valid Until', field: 'validUntil' }} />
      <DocumentPreview open={!!preview} onOpenChange={(v) => !v && setPreview(null)} doc={preview} type="quotation" />
    </div>
  );
}
